import type { OperationalMonth, OperationalStartPeriodConfig } from './types';

export const OPERATIONAL_MONTHS: OperationalMonth[] = [
  'JAN',
  'FEB',
  'MAR',
  'APR',
  'MAY',
  'JUN',
  'JUL',
  'AUG',
  'SEP',
  'OCT',
  'NOV',
  'DEC'
];

export const OPERATIONAL_MONTH_LABELS: Record<OperationalMonth, string> = {
  JAN: 'ENE',
  FEB: 'FEB',
  MAR: 'MAR',
  APR: 'ABR',
  MAY: 'MAY',
  JUN: 'JUN',
  JUL: 'JUL',
  AUG: 'AGO',
  SEP: 'SEP',
  OCT: 'OCT',
  NOV: 'NOV',
  DEC: 'DIC'
};

export const OPERATIONAL_START_PERIODS = {
  fallbackStartMonth: 'JAN' as OperationalMonth,
  cycles: {
    2026: {
      year: 2026,
      globalStartMonth: 'MAY',
      fallbackStartMonth: 'JAN',
      activationMode: 'HYBRID',
      vpOverrides: {},
      instanceOverrides: {},
      activationDetectionPolicy: {
        enabled: true,
        signals: [
          'ADMIN_ACTIVATED_AT',
          'ONBOARDING_COMPLETED_AT',
          'FIRST_CAPTURE',
          'FIRST_REPORT',
          'FIRST_PLAN',
          'FIRST_KPI',
          'INSTANCE_CREATED_AT'
        ],
        minimumConfidence: 'MEDIUM'
      },
      stalePolicy: {
        warningAfterMonths: 2,
        criticalAfterMonths: 3
      }
    },
    2027: {
      year: 2027,
      globalStartMonth: 'JAN',
      fallbackStartMonth: 'JAN',
      activationMode: 'HYBRID',
      vpOverrides: {},
      instanceOverrides: {},
      activationDetectionPolicy: {
        enabled: true,
        signals: [
          'ADMIN_ACTIVATED_AT',
          'ONBOARDING_COMPLETED_AT',
          'FIRST_CAPTURE',
          'FIRST_REPORT',
          'FIRST_PLAN',
          'FIRST_KPI',
          'INSTANCE_CREATED_AT'
        ],
        minimumConfidence: 'MEDIUM'
      },
      stalePolicy: {
        warningAfterMonths: 2,
        criticalAfterMonths: 3
      }
    }
  } satisfies Record<number, OperationalStartPeriodConfig>
};

export const getOperationalStartPeriodConfig = (year: number): OperationalStartPeriodConfig => {
  return (
    OPERATIONAL_START_PERIODS.cycles[year as keyof typeof OPERATIONAL_START_PERIODS.cycles] || {
      year,
      globalStartMonth: OPERATIONAL_START_PERIODS.fallbackStartMonth,
      fallbackStartMonth: OPERATIONAL_START_PERIODS.fallbackStartMonth,
      activationMode: 'HYBRID',
      vpOverrides: {},
      instanceOverrides: {},
      activationDetectionPolicy: {
        enabled: true,
        signals: ['FIRST_CAPTURE', 'FIRST_REPORT', 'FIRST_PLAN', 'FIRST_KPI', 'INSTANCE_CREATED_AT'],
        minimumConfidence: 'MEDIUM'
      },
      stalePolicy: {
        warningAfterMonths: 2,
        criticalAfterMonths: 3
      }
    }
  );
};

export const operationalMonthToIndex = (month: OperationalMonth): number => OPERATIONAL_MONTHS.indexOf(month);

export const indexToOperationalMonth = (index: number): OperationalMonth => {
  const normalized = Math.max(0, Math.min(11, index));
  return OPERATIONAL_MONTHS[normalized];
};

export const displayOperationalMonth = (month: OperationalMonth): string => OPERATIONAL_MONTH_LABELS[month];

export const parseSelectedMonthIndex = (month: string | number | null | undefined): number => {
  if (typeof month === 'number') return Math.max(0, Math.min(11, month));
  const raw = String(month || '').trim().toUpperCase();
  const aliases: Record<string, number> = {
    ENE: 0,
    JAN: 0,
    FEB: 1,
    MAR: 2,
    ABR: 3,
    APR: 3,
    MAY: 4,
    JUN: 5,
    JUL: 6,
    AGO: 7,
    AUG: 7,
    SEP: 8,
    OCT: 9,
    NOV: 10,
    DIC: 11,
    DEC: 11
  };
  return aliases[raw.substring(0, 3)] ?? new Date().getMonth();
};
